import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  DataListTableActionElement,
  DataListTableChipDataElement,
  DataListTableDataElement,
  DataListTableElement,
  DLT_DATA_TYPES,
} from '@el-data-list-table';
import { TranslateService } from '@ngx-translate/core';

import {
  AttachmentStatus,
  CHIP_BACKGROUND_COLORS,
  FILE_TYPES,
} from '@shared/constants';
import { IAttachmentDocument, IUploadFileToDMS } from '@shared/interfaces';
import { generateFileTypeRegex } from '@shared/utils';

import { FilesService } from '../../../../services/files.service';
import {
  SnackbarService,
  SUCCESS_TYPES,
} from '../../../../services/snackbar.service';
import { UserInfoResponseDTO } from '../../../auth/services';
import {
  DOCUMENT_DATA_STATUS,
  TYPES,
} from '../../../messages/popups/create-thread/create-thread.component';
import {
  CONFIRMATION_TYPES,
  GetUserConfirmationService,
} from '../../../shared/services/get-user-confirmation/service';

export interface IMultiDocumentUploader {
  maxSize: number;
  allowedTypesNames: string;
  allowedTypesRegex: RegExp;
  title: string;
  subtitle: string;
  icon?: string;
  loggedUserInfo: UserInfoResponseDTO;
  path: string;
  tags: string[];
  entity: string;
  firstFieldData?: { label: string; required: boolean };
  secondFieldData?: { label: string; required: boolean };
  uploadedDocuments?: IAttachmentDocument[];
  attachmentLimit?: number;
}

export interface IMultiDocumentUploaderResponse {
  attachments: IAttachmentDocument[];
  firstFieldValue?: string;
  secondFieldValue?: string;
}

const ACTIONS = {
  DELETE: 'DELETE',
  DOWNLOAD: 'DOWNLOAD',
};

@Component({
  selector: 'app-multi-document-uploader',
  templateUrl: './multi-document-uploader.component.html',
  styleUrls: ['./multi-document-uploader.component.scss'],
})
export class MultiDocumentUploaderComponent {
  documents: IAttachmentDocument[] = [];
  documentsDataList: DataListTableElement[] = [];
  files: File[] = [];

  uploadedFiles: string[] = [];
  imgPath: string;
  maxSize = 10;
  allowedTypesNames = '*.docx/ *.jpeg/ *.png/ *.csv and *.pdf';
  allowedTypesRegex = generateFileTypeRegex([
    FILE_TYPES.IMAGE,
    FILE_TYPES.DOCS,
    FILE_TYPES.CSV,
    FILE_TYPES.PDF,
  ]);
  title: string;
  subtitle: string;
  path: string;
  tags: string[];
  entity: string;
  documentsDataListInitialLength: number;

  toBeUploadedData: IUploadFileToDMS<File>[] = [];
  toBeUploadedDocuments: IAttachmentDocument[] = [];
  toBeDeletedData: IAttachmentDocument[] = [];
  isLoading: boolean;
  loggedUserInfo: UserInfoResponseDTO;

  attachmentLimit: number;

  documentUploadFormGroup = new FormGroup({
    firstField: new FormControl(null),
    secondField: new FormControl(null),
  });
  firstFieldName: string;
  firstFieldRequired: boolean;
  secondFieldName: string;
  secondFieldRequired: boolean;

  constructor(
    public translate: TranslateService,
    private snackbar: SnackbarService,
    private matDialog: MatDialogRef<MultiDocumentUploaderComponent>,
    private readonly filesService: FilesService,
    private readonly getUserConfirmation: GetUserConfirmationService,
    @Inject(MAT_DIALOG_DATA) public data: IMultiDocumentUploader
  ) {
    if (data) {
      this.maxSize = data?.maxSize;
      this.allowedTypesNames = data?.allowedTypesNames;
      this.allowedTypesRegex = data?.allowedTypesRegex;
      this.title = data?.title;
      this.subtitle = data?.subtitle;
      this.loggedUserInfo = data?.loggedUserInfo;
      this.path = data?.path;
      this.tags = data?.tags;
      this.entity = data?.entity;
      if (data?.uploadedDocuments) {
        this.data.uploadedDocuments.forEach((document) => {
          this.addToDataListTable({
            name: document.name,
            file_id: document.file_id,
            type: document.type,
            uploaded_date: document.uploaded_date,
            status: document.status,
          });
        });
      }
      this.attachmentLimit = data?.attachmentLimit || 100;
      if (data?.firstFieldData) {
        this.handleFieldData(
          data.firstFieldData,
          'firstFieldName',
          'firstFieldRequired',
          'firstField'
        );
      }
      if (data?.secondFieldData) {
        this.handleFieldData(
          data.secondFieldData,
          'secondFieldName',
          'secondFieldRequired',
          'secondField'
        );
      }
    }
  }

  handleFieldData = (
    fieldData: { label: string; required: boolean },
    fieldNameKey: string,
    fieldRequiredKey: string,
    controlName: string
  ) => {
    if (fieldData) {
      this[fieldNameKey] = fieldData.label;
      if (fieldData.required) {
        this[fieldRequiredKey] = true;
        this.documentUploadFormGroup.controls[controlName].setValidators([
          Validators.required,
        ]);
        this.documentUploadFormGroup.updateValueAndValidity();
      }
    }
  };

  triggerFileSelected(file: File) {
    const document: IAttachmentDocument = {
      name: file.name,
      uploaded_date: new Date(),
      type: 'pdf',
      status: AttachmentStatus.UPLOADING,
    };
    this.saveDocument(document, file);
  }

  saveDocument(document: IAttachmentDocument, file: File) {
    const index = this.documentsDataList.length;

    const entity: IUploadFileToDMS<File> = {
      is_public: false,
      is_system: true,
      generate_name: true,
      path: this.path,
      tags: this.tags,
      file,
      author_quota: this.loggedUserInfo.connection?.custom_quota,
    };

    const savedFile: IAttachmentDocument = {
      ...document,
      status: AttachmentStatus.PENDING_UPLOAD,
      file_id: (Math.random() * 25000).toString(),
    };

    this.updateDataListTableElement(index, savedFile);
    this.toBeUploadedData.push(entity);
    this.toBeUploadedDocuments.push(savedFile);
  }

  addToDataListTable(document: IAttachmentDocument) {
    const dataElements: DataListTableDataElement[] = [];
    const actionElements: DataListTableActionElement[] = [];
    dataElements.push(
      {
        key: this.translate.instant('knowledgebase.document.name'),
        value: document.name,
      },
      this.getStatusChip(document)
    );

    actionElements.push(this.createDeleteAction(document));

    this.documentsDataList.push({
      loading: document.status === AttachmentStatus.UPLOADING,
      expanded: document.status === AttachmentStatus.FAILED,
      data: dataElements,
      icon: 'description',
      actions: actionElements,
      index: this.documentsDataList.length,
    });
    this.documents.push(document);
  }

  async updateDataListTableElement(
    index: number,
    document: IAttachmentDocument
  ) {
    const dataElements: DataListTableDataElement[] = [];
    const actionElements: DataListTableActionElement[] = [];
    dataElements.push(
      {
        key: this.translate.instant('knowledgebase.document.name'),
        value: document.name,
      },
      this.getStatusChip(document)
    );
    actionElements.push(this.createDeleteAction(document));

    this.documentsDataList[index] = {
      loading: document.status === AttachmentStatus.UPLOADING,
      expanded: document.status === AttachmentStatus.FAILED,
      data: dataElements,
      icon: 'insert_drive_file',
      actions: actionElements,
      index,
    };

    this.documents[index] = document;
  }

  getStatusChip(document: IAttachmentDocument): DataListTableChipDataElement {
    let value;
    let chipBackgroundColor;
    switch (document.status) {
      case AttachmentStatus.UPLOADED:
        value = this.translate.instant('messages.document.uploaded');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.UPLOADED;
        break;

      case AttachmentStatus.FAILED:
        value = this.translate.instant('messages.document.failed');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.FAILED;
        break;

      case AttachmentStatus.UPLOADING:
        value = this.translate.instant('messages.document.uploading');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.UPLOADING;
        break;

      case AttachmentStatus.PENDING_DELETE:
        value = this.translate.instant('messages.document.pending-delete');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.PENDING_DELETE;
        break;

      case AttachmentStatus.PENDING_UPLOAD:
        value = this.translate.instant('messages.document.pending-upload');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.PENDING_UPLOAD;
        break;

      default:
        break;
    }

    return {
      key: this.translate.instant('messages.document.status'),
      value,
      type: DLT_DATA_TYPES.CHIP,
      chipBackgroundColor,
      chipColor: 'white',
    };
  }

  async uploadDocument() {
    let uploadDataLength = this.toBeUploadedData.length;

    let uploadData: IUploadFileToDMS<File>;

    for (uploadData of this.toBeUploadedData) {
      this.isLoading = true;
      const index = this.documentsDataList.length - uploadDataLength--;
      await new Promise<void>((resolve, reject) => {
        this.filesService.uploadFileToDMS(uploadData).subscribe({
          next: (res) => {
            this.isLoading = false;

            const file: IAttachmentDocument = {
              ...document,
              type: res.data.type,
              status: AttachmentStatus.UPLOADED,
              file_id: res.data._id.toString(),
              name: uploadData.file.name,
              uploaded_date: res.data.last_modified_on,
              storageId: res.data.storage.toString(),
            };
            this.updateDataListTableElement(index, file);
            this.snackbar.success(SUCCESS_TYPES.SAVED, this.entity);
            resolve();
          },
          error: () => {
            this.isLoading = false;
            const file: IAttachmentDocument = {
              ...document,
              status: AttachmentStatus.FAILED,
              name: '',
              type: '',
              uploaded_date: undefined,
            };
            this.updateDataListTableElement(index, file);
            this.snackbar.error(
              this.translate.instant('messages.document.upload-failed')
            );
            reject(
              new Error(
                this.translate.instant('messages.document.upload-failed')
              )
            );
          },
        });
      });
    }
  }

  async deleteFiles() {
    this.documentsDataList
      .map((obj, index) =>
        obj.data.some(
          (entry: { key: string; value: string }) =>
            entry.key === TYPES.STATUS &&
            entry.value === DOCUMENT_DATA_STATUS.PENDING_DELETE
        )
          ? this.removeFromDataListTable(index)
          : -1
      )
      .filter((index) => index !== -1);

    if (this.toBeDeletedData.length) {
      this.toBeDeletedData.forEach(async (data) => {
        const index = this.documents.findIndex((item) => item === data);
        await new Promise<void>((resolve, reject) => {
          this.filesService.deleteEntityFromDMS(data.file_id).subscribe({
            next: () => {
              this.documents.splice(index, 1);
              this.isLoading = false;
              resolve();
            },
            error: (e) => {
              this.isLoading = false;
              reject(e);
            },
          });
        });
      });
    }
  }

  removeFromDataListTable(index: number) {
    this.documentsDataList.splice(index, 1);
    this.documents.splice(index, 1);
    this.documentsDataList.forEach((element, index) => {
      element.index = index;
    });
  }

  action(e: { action: string; data: IAttachmentDocument; index: number }) {
    switch (e.action) {
      case ACTIONS.DELETE:
        this.deleteDocument(e.index, e.data);
        break;
      case ACTIONS.DOWNLOAD:
        this.downloadDocument(e.index, e.data);
        break;
      default:
        break;
    }
  }

  private createDeleteAction(
    document: IAttachmentDocument
  ): DataListTableActionElement {
    return {
      icon: 'delete',
      face: '',
      tooltip: this.translate.instant('knowledgebase.document.delete'),
      action: ACTIONS.DELETE,
      data: document,
    };
  }

  async uploadFilesToDMS() {
    this.isLoading = true;
    if (this.toBeUploadedData.length) await this.uploadDocument();
    if (this.toBeDeletedData.length) await this.deleteFiles();
    this.isLoading = false;
  }

  private deleteDocument(index: number, document: IAttachmentDocument) {
    if (document.status === AttachmentStatus.UPLOADING) {
      // should not be able to delete while uploading
      return;
    }

    if (document.status === AttachmentStatus.PENDING_UPLOAD) {
      this.documentsDataList.splice(index, 1);
      this.documents.splice(index, 1);
      return;
    }

    if (document.status === AttachmentStatus.FAILED) {
      // should be able to delete directly without confirmation
      this.documentsDataList.splice(index, 1);
      this.documents.splice(index, 1);
      return;
    }

    if (document.status === AttachmentStatus.UPLOADED) {
      const dialogData = {
        description: this.translate.instant(
          'knowledgebase.document.description'
        ),
      };

      this.getUserConfirmation
        .confirm(CONFIRMATION_TYPES.DELETE, dialogData)
        .then(async (res: boolean) => {
          this.toBeDeletedData.push(document);

          if (res) {
            const savedFile: IAttachmentDocument = {
              ...document,
              status: AttachmentStatus.PENDING_DELETE,
            };

            this.updateDataListTableElement(index, savedFile);
          } else {
            this.snackbar.error(
              this.translate.instant('messages.snackbar.cant-delete')
            );
          }
        });
    }
  }

  private downloadDocument(index: number, document: IAttachmentDocument) {
    this.changeLoadingState(index, true);
    this.filesService
      .downloadEntityFromDMS(document.file_id, document.name)
      .then(() => {
        this.changeLoadingState(index, false);
        this.snackbar.success(
          SUCCESS_TYPES.DOWNLOADED,
          this.translate.instant('knowledgebase.file')
        );
      })
      .catch(() => {
        this.changeLoadingState(index, false);
        this.snackbar.error(
          this.translate.instant('knowledgebase.document.download-failed')
        );
      });
  }

  private changeLoadingState(index: number, is_loading: boolean) {
    this.documentsDataList[index].loading = is_loading;
    this.documentsDataList[index].expanded = !is_loading;
  }

  onCancel(): void {
    this.matDialog.close();
  }

  async onSave() {
    if (this.documentsDataList.length > 0) {
      await this.uploadFilesToDMS();
      this.matDialog.close({
        attachments: this.documents,
        ...(this.firstFieldName && this.documentUploadFormGroup.value.firstField
          ? { firstFieldValue: this.documentUploadFormGroup.value.firstField }
          : {}),
        ...(this.secondFieldName &&
        this.documentUploadFormGroup.value.secondField
          ? { secondFieldValue: this.documentUploadFormGroup.value.secondField }
          : {}),
      });
    }
  }
}
